/**
 * @module PConnect
 */
/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */

import ContextProcessor from '../context/context-processor';
import AnnotationProcessor from '../annotation/processor';
import AssetLoader from '../utils/asset-loader';
import * as StateManager from '../store/state/state-manager';
import * as ContainerUtils from '../container/container-utils';
import RouteParser from '../router/route-parser';
import Utils from '../utils/utils';
import ValidationApi from '../validation/validation-api';
import Validator from '../validation/validator';
import { addPageFieldToForm } from '../utils/form-handler';
import * as RoutingManager from '../router/routing-manager';
import constants, { publicConstants } from '../constants';
import ActionsApi from '../actions/api';
import ContainerManager from '../container/container-manager';
import services from '../services';
import * as MessageManager from '../messages';
import { ActionManagerInstance } from '../actions/manager';
import { getAdditionalInfo, getPropertyMetadata } from '../rules/rules-store';
import { error, mergeData } from '../store/state/actions/action-creators';
import CaseInfo from '../case/case-info';
import { inspectComponentForRefreshTargets, recordViewForRefresh } from '../refresh/refreshHelpers';
import * as CaseUtils from '../utils/case-utils';
import { generatePageInstructionsForNestedPayload, listActions } from '../page-instructions';
import CaseAPIs from '../case/apis';
import AnnotationUtils from '../annotation/utils';
import fieldDefaultUtils from '../fields/field-default';
import StateUtils from '../store/state/utils/state-utils';
import { updateChangeList } from '../store/state/middlewares/property-change-middleware';
import ContextTreeManager from '../contextree/treemanager';
import type { PropertyModel } from '../rules/types';
import type { Message, NodeProps } from '../contextree/types';
import environmentInfo from '../environment-info';
import FieldUtils from '../fields';
import ParagraphUtils from '../dynamic-content/util';
import { ConfigFallbackFactory } from '../config-fallback-factory';
import { getLocaleReference, LocalizationService } from '../locale/LocalizationService';
import DatapageDependencyMap from '../cascade/datapage-dependency-handler';
import { fetchViewResources } from '../utils/view-resources';
import { LocaleRuleTypes } from '../types';

import getChildrenIterator from './iterator/children';
import { C11nEnvUtils } from './utils';
import type {
  Meta,
  Options,
  Config,
  ComponentMetadataConfig,
  FieldConfigData,
  FieldMetadata,
  CreateC11nEnv
} from './types';

/**
 * The errorMsgConfig types defines a JSON configuration object used to specify error messages in the store.
 * It is used as a parameter to {@link C11nEnv.clearErrorMessages} to indicate which messages should be cleared.
 * @example <caption>errorConfig to clear messages on a context</caption>
 * {category: "HTTP", context: "app/primary"}
 * This will clear the error messages at the "app/primary" level which belong to category of type HTTP (like 4XX errors).
 *
 * @example <caption>errorConfig to clear messages on AppShell</caption>
 * {category: "HTTP"}
 * This will clear the error messages at the App level which belongs to category HTTP.
 *
 * @example <caption>errorConfig to clearing messages on FirstName property</caption>
 * {property: ".firstName"}
 * This will clear the error messages related to the property: firstName.
 */
type ErrorMsgConfig = {
  /** type of message that needs to be deleted (category types - HTTP (App or top level), PAGE (use this category for messages above the assignment)) */
  category?: string;
  /** on which messages needs to be cleared. Can be empty in which case error messages on app container are cleared. */
  context?: string;
  /** name on which messages needs to be cleared. */
  property: string;
  /** error subtype on which messages needs to be cleared. */
  subType?: string;
};

let componentCreator: Function;
// eslint-disable-next-line sonarjs/cognitive-complexity
const resolveStateProps = (config: any, stateObj: any, parentstateObjInfo?: any) => {
  if (!config) {
    return;
  }
  Object.keys(config).forEach((key) => {
    if (key !== 'children') {
      // Don't process children
      const valueWithAnnotation = config[key];
      const value = AnnotationProcessor.trimAnnotation(valueWithAnnotation);
      if (
        (typeof value === 'string' || typeof valueWithAnnotation === 'string') &&
        ((Utils.isValidAnnotation(valueWithAnnotation) && Utils.isPropertyRef(value)) ||
          valueWithAnnotation.startsWith('@W ') ||
          valueWithAnnotation.startsWith('@DW ') ||
          valueWithAnnotation.startsWith('@E ') ||
          valueWithAnnotation.startsWith('@ATTACHMENT '))
      ) {
        if (parentstateObjInfo && parentstateObjInfo.parentKey) parentstateObjInfo.isObjHasValue = true;
        stateObj[key] = value;
      } else if (Array.isArray(value)) {
        value.forEach((p, i) => {
          const parentKeyObjToSend = {
            parentStateObj: stateObj,
            parentKey: key,
            isObjHasValue: !!stateObj[key]
          };
          stateObj[key] = stateObj[key] || [];
          stateObj[key][i] = Object.create({});
          resolveStateProps(p, stateObj[key][i], parentKeyObjToSend);
        });
      } else if (value && typeof value === 'object' && value.type !== 'exp') {
        const parentKeyObjToSend = {
          parentStateObj: stateObj,
          parentKey: key,
          isObjHasValue: !!stateObj[key]
        };
        stateObj[key] = stateObj[key] || {};
        resolveStateProps(value, stateObj[key], parentKeyObjToSend);
      }
    }
  });
  if (parentstateObjInfo && !parentstateObjInfo.isObjHasValue) {
    delete parentstateObjInfo.parentStateObj[parentstateObjInfo.parentKey];
  }
};

const mergeInheritedProps = (_inheritedConfig: any, rawConfig: any) => {
  _inheritedConfig._inheritedProps = _inheritedConfig._inheritedProps || [];
  _inheritedConfig.inheritedProps = [..._inheritedConfig._inheritedProps, ...Utils.deepClone(rawConfig.inheritedProps)];
};

/**
 *
 */
export class C11nEnv {
  public useCustomContext?: boolean;
  public _rawConfig?: ComponentMetadataConfig;
  public _classID: string | null;
  public index?: number;
  public _ruleClass: string | undefined;
  private readonly meta?: Meta;
  private readonly _isRoot: boolean | undefined;
  private readonly options: Options;
  private readonly _isExpressionExist: boolean;
  private readonly _isWhenExist: boolean;
  private readonly _isValidatorExist: boolean;
  private readonly _pageReference: string;
  private readonly _referenceContext: string;
  private readonly _contextName: string;
  private readonly _stateProps: any;
  private _actions: { [key: string]: Function };
  private readonly _children: Meta['children'];
  private readonly _additionalRegisteredProps: any;
  private _caseInfo: CaseInfo | null;
  private readonly viewName: string | undefined;
  private readonly parentViewName: string;
  private _referenceList: string;
  private readonly _isInsideList: boolean;
  private _inheritedConfig: any;
  private readonly _localeRuleName: string | undefined;
  private readonly _containerTarget: string | undefined;
  private readonly _readOnly: boolean | string | undefined;
  private _hasSuggestions: boolean;
  private readonly _isState: boolean;
  private readonly _type: string | undefined;
  private _actionsApi: ActionsApi | null;
  private readonly categoryName: string;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor(config: Config) {
    const { meta, isRoot, options = {}, useCustomContext } = config;
    this.meta = meta;
    this._isRoot = isRoot;
    this.options = options;
    this.useCustomContext = useCustomContext;
    this._rawConfig = meta?.config;
    this._isExpressionExist = false;
    this._isWhenExist = false;
    this._isValidatorExist = false;
    this._pageReference = '';
    this._referenceContext = '';
    this._contextName = '';
    this._stateProps = {};
    this._actions = {};
    this._children = '';
    this._additionalRegisteredProps = {};
    this._caseInfo = null;
    this.viewName = options.viewName;
    this.parentViewName = options.viewName ?? '';
    this._referenceList = options.referenceList || '';
    this._isInsideList = options.isInsideList || false;
    this._inheritedConfig = {};
    this._localeRuleName = options.localeReference;
    this._containerTarget = options.target;
    this._readOnly = meta?.config?.readOnly || options.readOnly;
    this._hasSuggestions = false;
    this._actionsApi = null;
    this._classID = null;
    this.index = config.index;
    this.categoryName = options.categoryName || '';
    if (options.inheritedConfig) {
      this._inheritedConfig = Utils.deepClone(options.inheritedConfig);
    }
    if (meta?.config?.inheritedProps) {
      mergeInheritedProps(this._inheritedConfig, meta?.config);
    }

    if (
      this.meta?.config?.value &&
      typeof this.meta?.config?.value === 'string' &&
      this.meta?.config?.value?.startsWith('@P ') &&
      this.meta?.config?.value?.includes('(')
    ) {
      this.meta.config.value = FieldUtils.formatePageReferenceToArrayIndex(this.meta.config.value);
    }
    resolveStateProps(this._rawConfig, this._stateProps);
    /**
     * Returns true if there is any state associated with this object
     * @private
     */
    this._isState = !Utils.isObjectEmpty(this._stateProps);
    if (this._rawConfig && this._rawConfig.config) {
      this._isExpressionExist = Utils.isExpressionExist(this._rawConfig.config);
      this._isWhenExist = Utils.isWhenExist(this._rawConfig.config);
      this._isValidatorExist = Validator.isValidatorExist(this._rawConfig.config);
    }

    // [TO-DO] need to remove app/primary_0 hardcode check after viewContainer implementation
    if (this._isRoot) {
      this._contextName = publicConstants.APP.APP;
    } else {
      this._contextName = this.meta?.config?.contextName || this.options.context || '';
    }

    // Process meta
    this._type = this.meta?.type;
    let optionalPageReference = this.options.pageReference;
    if (this.options.pageReference) {
      // resolving pageReference that is passed as options
      optionalPageReference = AnnotationProcessor.handleAnnotation(this.options.pageReference, this) as
        | string
        | undefined;
    }
    this._pageReference = ContextProcessor.getContextPath(
      (optionalPageReference || this.options.parentPageReference) as string, // considering parent page reference when current configured reference is invalid
      this._pageReference
    );

    if (!this._pageReference) {
      this._pageReference = '';
    }

    if (this._type === 'reference') {
      this._rawConfig = meta;
    }

    const hasWhen = this._type === 'reference' && Utils.isWhenExist({ visibility: this.meta?.config?.visibility });

    const hasDecisionParagraph = this._checkAndSetDecisionParagraph('instructions', this._rawConfig?.instructions);

    let hasDecisionLabel = false;

    if (this._type !== 'reference' && meta) {
      this._rawConfig = ContextProcessor.resolveReference(meta, this);
    }
    if (this._rawConfig) {
      // Process conditions
      const metaConfig = this.getRawConfigProps();

      if (metaConfig) {
        this._localeRuleName =
          AnnotationProcessor.trimAnnotation(metaConfig.localeReference) || options.localeReference;
        this._isExpressionExist = Utils.isExpressionExist(metaConfig);
        this._isWhenExist = Utils.isWhenExist(metaConfig);
        this._isValidatorExist = Validator.isValidatorExist(metaConfig);

        hasDecisionLabel = this._checkAndSetDecisionParagraph('label', metaConfig?.label);
      }

      this._children = this._rawConfig.children;

      // Populate view Name
      if (this._rawConfig?.type?.toLowerCase() === constants.VIEW && this._rawConfig.name) {
        this.viewName = AnnotationProcessor.handleAnnotation(this._rawConfig.name, this) as string | undefined;
      }

      // If component is present in form, then only inspect for refresh targets.
      if (this.options.hasForm) {
        inspectComponentForRefreshTargets(this);
      }
    }

    if (hasWhen || hasDecisionParagraph || hasDecisionLabel) {
      recordViewForRefresh(this.viewName, this._contextName, this._pageReference);
    }
  }

  /**
   * This function returns if the paragraph is decision based and if it is then adds it to state props
   * @private
   */
  _checkAndSetDecisionParagraph(propName: string, propValue: string | undefined) {
    let isDecisionPara = false;
    if (propValue && propValue.startsWith(constants.PARAGRAPH_ANNOTATION)) {
      const paragraphName = AnnotationProcessor.trimAnnotation(propValue);
      isDecisionPara = ParagraphUtils.isDecisionParagraph(paragraphName, this.getCurrentClassID());

      if (isDecisionPara) {
        this.setStateProps({ [propName]: propValue });
      }
    }
    return isDecisionPara;
  }

  /**
   * This function returns if the field is configured with declarative target or not
   * @private
   */
  isDeclarativeTarget() {
    let isDeclarativeTarget = false;
    if (this.getValue(this._pageReference) || this._pageReference === '') {
      const propertyName = AnnotationProcessor.trimAnnotation(this.getPropertyName());
      const fieldMetadata = this.getFieldMetadata(propertyName);
      isDeclarativeTarget = fieldMetadata?.isDeclarativeTarget || false;
    }
    return isDeclarativeTarget;
  }

  _getPropertyName() {
    const propertyName = AnnotationProcessor.trimAnnotation(this.getPropertyName());
    return this._pageReference === '' && propertyName.startsWith('.')
      ? propertyName.substring(1, propertyName.length)
      : propertyName;
  }

  addFieldNode() {
    const propertyName = this._getPropertyName();
    const { value, label, required, readOnly, disabled, visibility, validationType, caption, allowMultiple } =
      this.getRawConfigProps();

    if (this.getValue(this._pageReference) || this._pageReference === '') {
      const associatedView =
        this._type === 'Address' && this._rawConfig?.config?.associatedView
          ? AnnotationProcessor.trimAnnotation(this._rawConfig.config.associatedView)
          : '';
      const fieldLabel = label || caption;
      const options: NodeProps = {
        target: this._containerTarget,
        context: this._contextName,
        pageReference: this._pageReference,
        propertyName: AnnotationProcessor.trimAnnotation(value),
        label: fieldLabel?.startsWith('@')
          ? AnnotationProcessor.handleAnnotation(fieldLabel, this, undefined)
          : fieldLabel,
        required,
        readOnly,
        disabled,
        visibility: visibility || this.options?.visibility,
        skipRequiredValidation: this.options?.isBulkAction,
        inheritedProps: this.getInheritedProps(),
        useCustomContext: this.useCustomContext,
        value,
        validationType,
        componentName: this._rawConfig?.type,
        hasSuggestions: this._hasSuggestions,
        isInsideList: this.isInsideList(),
        index: this.index,
        allowMultiple,
        isDeclarativeTarget: this.isDeclarativeTarget()
      };
      ContextTreeManager.addFieldNode(
        this._contextName,
        this._pageReference,
        (associatedView || this.categoryName || this.viewName) ?? '',
        propertyName,
        this.getValue(propertyName),
        options
      );
    }
  }

  removeNode() {
    if (!this.isVisible()) {
      if (Object.hasOwn(this._rawConfig?.config ?? {}, 'value') && this._type !== 'Address') {
        ContextTreeManager.removeFieldNode(
          this._contextName,
          this._pageReference,
          this.viewName || '',
          AnnotationProcessor.trimAnnotation(this.getPropertyName()),
          this.index as number
        );
      } else if (this._type === 'Address' && this._rawConfig?.config?.associatedView) {
        // remove address node and its children
        ContextTreeManager.removeViewNode(
          this._contextName,
          this._pageReference,
          AnnotationProcessor.trimAnnotation(this._rawConfig.config.associatedView),
          this.index as number
        );
      } else {
        // remove view node and its children
        const pageRef = this._rawConfig?.config?.context
          ? `${this._pageReference}${this._rawConfig?.config.context}`
          : this._pageReference;
        ContextTreeManager.removeViewNode(
          this._contextName,
          pageRef,
          this._rawConfig?.config?.name || this._rawConfig?.config?.id || '',
          this.index
        );
      }
    }
  }

  addViewNode() {
    if (this.isReadOnly()) {
      return;
    }
    if (this._rawConfig?.type?.toLowerCase() === constants.VIEW && this._rawConfig.name && this.viewName) {
      ContextTreeManager.addViewNode(this._contextName, this._pageReference, this.viewName, this.parentViewName);
    }
    if (this._rawConfig?.type === 'Group' && this._rawConfig?.config?.id) {
      ContextTreeManager.addViewNode(
        this._contextName,
        this._pageReference,
        this._rawConfig.config.id,
        this.parentViewName
      );
    }
    if (this._rawConfig?.type === 'Address' && this._rawConfig?.config?.associatedView) {
      ContextTreeManager.addViewNode(
        this._contextName,
        this._pageReference,
        AnnotationProcessor.trimAnnotation(this._rawConfig.config.associatedView),
        this.parentViewName
      );
    }
    if (this._rawConfig?.type?.toLowerCase() === 'deferload' && this._rawConfig.config?.name) {
      ContextTreeManager.addViewNode(
        this._contextName,
        this._pageReference,
        this._rawConfig.config.name,
        this.parentViewName
      );
    }
  }

  addWhenNode() {
    let referenceName: string | undefined = '';
    let type = '';
    const whenNames = Utils.getWhenNames(this._rawConfig?.config);
    const configProps = this.getRawConfigProps();
    if (configProps?.type && configProps?.type?.toLowerCase() === constants.VIEW) {
      type = constants.CONTEXT_TREE_NODE_TYPES.VIEW;
      referenceName = this.getCurrentView();
    } else {
      type = constants.CONTEXT_TREE_NODE_TYPES.FIELD;
      const propName = this.getPropertyName();
      referenceName = AnnotationProcessor.isProperty(propName) ? AnnotationProcessor.trimAnnotation(propName) : '';
    }
    if (referenceName) {
      whenNames.forEach((value) => {
        ContextTreeManager.addWhenNode(this.getContextName(), this.getPageReference(), type, referenceName, value, {
          pageReference: this.getPageReference(),
          context: this.getContextName()
        });
      });
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  updateContextTree() {
    if (this.isVisible()) {
      this.addViewNode();
      if (
        this._rawConfig?.config &&
        Object.hasOwn(this._rawConfig.config, 'value') &&
        this._inheritedConfig.displayMode !== 'DISPLAY_ONLY' &&
        this._rawConfig.config?.displayMode !== 'DISPLAY_ONLY'
      ) {
        this.addFieldNode();
      }
      if (
        (this._rawConfig?.type?.toLowerCase() !== constants.VIEW ||
          this._rawConfig.config?.renderMode === 'Editable') &&
        this._rawConfig?.config?.referenceList &&
        this._rawConfig.config?.displayMode !== 'DISPLAY_ONLY'
      ) {
        const {
          value,
          label,
          required,
          readOnly,
          disabled,
          visibility,
          validationType,
          selectionMode,
          selectionList,
          selectionKey,
          referenceList,
          renderMode,
          caption,
          uniqueField
        } = this.getRawConfigProps();
        const fieldLabel = label || caption;

        let selfReferencingDatapageKeys: string[] = [];
        if (['Multiselect', 'Checkbox'].includes(this._rawConfig.type as string)) {
          selfReferencingDatapageKeys = this.getSelfReferencingDatapageKeys();
        }

        ContextTreeManager.addPageListNode(
          this._contextName,
          this._pageReference,
          this.viewName ?? '',
          AnnotationProcessor.trimAnnotation(this.getPropertyName()),
          {
            target: this._containerTarget,
            context: this._contextName,
            pageReference: this._pageReference,
            propertyName: AnnotationProcessor.trimAnnotation(this.getPropertyName()),
            label: fieldLabel?.startsWith('@')
              ? AnnotationProcessor.handleAnnotation(fieldLabel, this, undefined)
              : fieldLabel,
            required,
            readOnly,
            disabled,
            visibility: visibility || this.options.visibility,
            skipRequiredValidation: this.options.isBulkAction,
            inheritedProps: this.getInheritedProps(),
            useCustomContext: this.useCustomContext,
            value,
            validationType,
            componentName: this._rawConfig.type,
            type: this._rawConfig.config?.type,
            hasSuggestions: this._hasSuggestions,
            isInsideList: this.isInsideList(),
            selectionMode,
            selectionList,
            renderMode,
            referenceList,
            selectionKey,
            index: this.index,
            uniqueField,
            selfReferencingDatapageKeys: ['Multiselect', 'Checkbox'].includes(this._rawConfig.type as string)
              ? selfReferencingDatapageKeys
              : undefined
          }
        );
        const values =
          this.getValue(AnnotationProcessor.trimAnnotation(this.getPropertyName()), this._pageReference) || [];
        ContextTreeManager.updatePageListNode(
          this._contextName,
          this._pageReference,
          AnnotationProcessor.trimAnnotation(this.getPropertyName()),
          values.length
        );
      }
    }
    if (this.isWhenExist()) {
      this.addWhenNode();
    }

    this.removeNode();
  }

  /**
   * Returns the properties which are bound to state, in sense these properties
   * are reactive.
   * @function getStateProps
   *
   * @example
   * Sample:
   * {
   *   "FirstName": "test value"
   * }
   * @example <caption>getPConnect().getStateProps()</caption>
   * const props = getPConnect().getStateProps();
   *
   * @returns the properties that are bound to the Redux state
   */
  getStateProps(): any {
    return this._stateProps;
  }

  /**
   * Set the properties which are bound to state, in sense these properties
   * are reactive.
   * @example <caption>getPConnect().setStateProps({'datasourceMetadata':'@FIELD_META .Test'})</caption>
   * @param propsObj object containing each prop name and its value
   * @private
   */
  setStateProps(propsObj: any) {
    Utils.mergeDeep(propsObj, this._stateProps);
  }

  /**
   * Returns the metadata, this is raw, unresolved metadata
   * @returns - metadata
   * @private
   */
  getMetadata() {
    return this._rawConfig;
  }

  /**
   * Returns shared datapage record, reads data page name from referenceList in view metadata along with parameters
   * @returns - data page records or undefined if no data page found
   * @private
   */
  getSharedDataPageForReferenceList() {
    const classId = this.getCaseInfo().getClassName();
    const viewName = this.getCurrentView();

    if (!viewName || !classId) return;
    const view = fetchViewResources(viewName, this, classId);
    if (!view) return;
    const dataPageName = this.getRawMetadata()?.config?.referenceList;
    if (!dataPageName) return;

    let parameters = {};
    if (typeof view.config?.parameters === 'object' && view.config.parameters !== null) {
      parameters = view.config.parameters as Record<string, string>;
    }
    const dataPageWithHash = Utils.generateDataPageHashWithParameters(parameters, dataPageName, this);
    const sharedDataObject = StateManager.getSharedDataObject();
    const sharedData = sharedDataObject ? sharedDataObject[dataPageName]?.[dataPageWithHash] : {};
    return Array.isArray(sharedData?.pxResults) ? sharedData.pxResults : undefined;
  }

  /**
   * Returns instance of CaseInfo class bound to redux store current container context
   * @function getCaseInfo
   * @returns Returns instance of the CaseInfo class
   */
  getCaseInfo(): CaseInfo {
    if (!this._caseInfo) {
      this._caseInfo = new CaseInfo(this);
    }
    return this._caseInfo;
  }

  /**
   * Returns returns case reference string as per current case context.
   * @function getCaseLocaleReference
   * @param caseClass optional parameter to pass case class or data class to get locale reference for. If not passed, it will be derived from case context.
   * @returns {String} Returns locale reference for the case type.
   * @example <caption>In the example below</caption>
   * getPconnect().getCaseLocaleReference() returns CASE!SERVICEREQUEST
   * @private
   */
  getCaseLocaleReference(caseClass?: string): string | null {
    const rawConfigProps = this.getRawConfigProps();
    const caseClassName =
      caseClass ||
      this.getValue(constants.getProperty(constants.CASE_INFO.CASE_TYPE_ID)) ||
      this.getValue(constants.CLASS_ID, constants.getProperty(constants.DATA_INFO.DATA_INFO_CONTENT)) ||
      rawConfigProps?.referenceClass ||
      rawConfigProps?.className;
    return caseClassName ? getLocaleReference(LocaleRuleTypes.CASE, caseClassName) : null;
  }

  /**
   * Obtains an entry point to the ListActions class that contains APIs that handle list related actions for
   * components that display page list data and allow modifications on the list.
   * @function getListActions
   * @example  <caption>In the example </caption>
   * getPConnect().getListActions();
   */
  getListActions() {
    return listActions(this);
  }

  getReferenceList() {
    return this._referenceList;
  }

  isInsideList() {
    return this._isInsideList || /\[\d+\]/g.test(this.getPageReference()) || /\[\d+\]/g.test(this.getPropertyName());
  }

  /**
   * <strong>For Advanced Use Only</strong><br>
   * Returns the component's complete metadata in its raw and unresolved format.
   * This API should only
   * be used in advanced use cases where a component needs the complete,
   * unresolved metadata. <strong>In most situations, the component should
   * only require getConfigProps() to get the resolved configuration metadata of
   * the component.</strong><br><br>
   * <strong>NOTE: the format and content of the metadata returned
   * may change as the DX API changes.</strong>
   *
   * @function getRawMetadata
   * @returns The raw, unresolved metadata for the component
   * @public
   *
   * @example  <caption>In the example below, we see the raw, unresolved metadata for a simple "Table" component.</caption>
   *
   * {
   *   "type": "Table",
   *   "config": {
   *     "referenceList": "D_pyMyWorkList.pxResults",
   *     "rowClickAction": "openAssignment",
   *     "personalizationId": "pyMyWork-D_pyMyWorkList",
   *     "fields": [
   *       {
   *         "type": "Text",
   *         "config": { "label": "@L Priority", "formatType": "number", "value": "@P .pxUrgencyAssign" }
   *       },
   *       {
   *         "type": "Text",
   *         "config": { "label": "@L ID", "value": "@P .pxRefObjectInsName" }
   *       },
   *       {
   *         "type": "Text",
   *         "config": { "label": "@L Label", "value": "@P .pyLabel" }
   *       },
   *       {
   *         "type": "DateTime",
   *         "config": { "sortDirection": "DESC", "sortPriority": "1", "label": "@L Creation date/time", "value": "@P .pxCreateDateTime" }
   *       },
   *       {
   *         "type": "Text",
   *         "config": { "label": "@L Status", "value": "@P .pyAssignmentStatus" }
   *       }
   *     ]
   *   }
   * }
   *
   */
  getRawMetadata() {
    return this.getMetadata();
  }

  setReferenceList(ref: string) {
    this._referenceList = ref;
  }

  /**
   * Returns the component's configuration information
   * @function getComponentConfig
   * @example
   * example: {
   *    label: "@P .FirstName"
   * }
   *
   * @example <caption>getPConnect().getComponentConfig()</caption>
   * const config = getPConnect().getComponentConfig();
   * the value of config gets the configuration of the component.
   *
   * @returns component configuration information
   * @private
   */
  getComponentConfig(): any {
    return this.getMetadata()?.config || {};
  }

  /**
   * Checks whether component has expression configured
   * or not.
   *
   * @function isExpressionExist
   * @returns true or false
   * @private
   */
  isExpressionExist(): boolean {
    return this._isExpressionExist;
  }

  /**
   * Checks whether component has when configured
   * or not.
   * @function isWhenExist
   * @returns true or false
   * @private
   */
  isWhenExist(): boolean {
    return this._isWhenExist;
  }

  /**
   * This APIchecks if the component is associated with a condition (such as an expression or when condition) and returns true if anyone of them exists.
   * @function isConditionExist
   * @example <caption> Usage of isConditionExist - getPConnect().isConditionExist()</caption>
   * Usage: const isConditionConfigured = getPConnect().isConditionExist();
   * value of isConditionConfigured will be true or false based on current component configuration.
   *
   * @returns true or false
   * @public
   */
  isConditionExist(): boolean {
    return this.isExpressionExist() || this.isWhenExist();
  }

  /**
   * Checks whether current component is bound to state or not.
   * If component has any property reference, it will be treated
   * as bound to state.
   *
   * @function isBoundToState
   *
   * @example <caption>getPConnect().isBoundToState()</caption>
   * const isStateBound = getPConnect().isBoundToState();
   * In this example, the API returns a Boolean value which denotes if the current component is bound to a Redux State.
   *
   * @returns True if bound to state
   */
  isBoundToState(): boolean {
    return this._isState;
  }

  /**
   * Returns the data reference path of the store,
   * where the data value has been stored for current component.
   * @function getPageReference
   *
   * @example
   * For example:
   * @example <caption> While loading a portal(App level)</caption>
   * pageReference value is: "pyPortal"
   * @example <caption> Single component </caption>
   * For components like 'FirstName, LastName', pageReference value is: "caseInfo.content"
   * @example <caption> Embedded components which are under embedded view </caption>
   * For components like 'ZipCode, StreetName' which are under Address view, pageReference value is :"caseInfo.content.Address"
   *
   * @returns pageReference path
   * @public
   */
  getPageReference(): string {
    return this._pageReference;
  }

  getPropertyName(): string {
    const componentConfig = this.getComponentConfig();
    let propKey;
    if (componentConfig.selectionMode === 'multi') {
      propKey = 'selectionList';
    } else if (componentConfig.selectionMode === 'single' && componentConfig.selectionKey) {
      propKey = 'selectionKey';
    } else if (componentConfig.renderMode === 'Editable') {
      propKey = 'referenceList';
    } else if (this._type === 'Address') {
      propKey = 'addressProp';
    } else {
      propKey = 'value';
    }
    return this.getComponentConfig()[propKey];
  }

  getFullReference(): string {
    const pageReference = this.getPageReference();
    const prop = this.getPropertyName();
    return ContextProcessor.getContextPath(pageReference, prop);
  }

  /**
   * this function returns the context name under which the component has rendered.
   *
   * @public
   * @function getContextName
   * @returns context name
   *
   * @example <caption>Example for getContextName()</caption>
   * const context = getPConnect().getContextName();
   * //example output : returns => 'app/primary_1'
   *
   */
  getContextName(): string {
    return this._contextName;
  }

  /**
   * this function returns the Container name with which the component is associated
   *
   * @public
   * @function getContainerName
   * @returns container name
   *
   * @example <caption>Example for getContainerName()</caption>
   * const { containerName = "primary" } = getPConnect().getContainerName();
   * //example output : returns => 'primary'
   *
   */
  getContainerName(): string {
    const { type } = this.getMetadata() || {};
    const containers = [
      constants.CONTAINERS.ROOT_CONTAINER,
      constants.CONTAINERS.VIEW_CONTAINER,
      constants.CONTAINERS.FLOW_CONTAINER,
      constants.CONTAINERS.MODAL_VIEW_CONTAINER,
      constants.CONTAINERS.PREVIEW_VIEW_CONTAINER,
      constants.CONTAINERS.HYBRID_VIEW_CONTAINER
    ];
    const { acTargetName = ContainerUtils.CONTAINER_NAMES.PRIMARY, name, routingInfo } = this.getComponentConfig();
    if (((type && containers.includes(type)) || routingInfo === '@ROUTING_INFO') && name) {
      return name;
    }

    return this.options.containerName || acTargetName;
  }

  /**
   * this function returns the fully qualified target container name with which the component is associated
   *
   * @public
   * @function getTarget
   * @returns target name
   *
   * @example <caption>Example for getTarget()</caption>
   * const target = getPConnect().getTarget();
   * // example output : returns => 'app/primary'
   */
  getTarget() {
    return this._containerTarget;
  }

  /**
   * This API obtains the name of the view under which the current component was rendered.This API returns the name of the views a string.
   * @public
   * @function    getCurrentView
   * @returns view name
   * @example     <caption>Example for getCurrentView()</caption>
   * In this example, the API returns the name of the view under which the current component was rendered.
   * const viewName = getPConnect().getCurrentView();
   */
  getCurrentView() {
    return this.viewName;
  }

  /**
   * Populates suggestion props if config has value prop and redux store has field in suggestion context.
   *
   * @param configObj configObj to which hasSuggestion props gets added
   * @function populateSuggestionProps
   * @example <caption>Usage: getPConnect().populateSuggestionProps({ label: 'label-name' })  </caption>
   * Example Usage:
   * const configProps = { label: 'label-name' }
   * getPConnect().populateSuggestionProps(configProps);
   *
   * After the above populateAdditionalProps function call, the configProps object value gets updated by suggestionProps
   * all the possible keys added to the configObject are:
   * configObject.hasSuggestions (string) - hasSuggestion value.
   *
   * @private
   */
  populateSuggestionProps(configObj: { [key: string]: any }) {
    if (
      this._rawConfig?.config &&
      Object.hasOwn(this._rawConfig.config, 'value') &&
      this.getStateProps()?.value &&
      this.options.hasForm
    ) {
      const isSuggestionContextExist =
        StateManager?.getStore()?.getState()?.data[`${this.getContextName()}/${constants.SUGGESTION}`];
      const suggestionProp = this.getStateProps()?.value;
      const fullSuggestionPropRef = ContextProcessor.getContextPath(this.getPageReference(), suggestionProp);
      const propValue = isSuggestionContextExist
        ? StateUtils.getSuggestionsContext(this.getContextName()).getField(fullSuggestionPropRef)
        : undefined;
      this._hasSuggestions = !!propValue;
      configObj.hasSuggestions = this._hasSuggestions;
      // stamping the hassuggestion value in stateprops so that it will rerender in condition
      this._stateProps.hasSuggestions = this._hasSuggestions;
    }
  }

  /**
   * Returns the object with resolved component
   * configuration of the component.
   * @function getConfigProps
   *
   * @example
   * Example:
   * {
   *   label: "@P .FirstName"
   * }
   * output:
   * {
   *   label: "Hello Name"
   * }
   *
   * @example <caption>getPConnect().getConfigProps()</caption>
   * @param destinationObject the object to which config props gets added.
   * const props = getPConnect().getConfigProps();
   *
   *
   * @returns resolved component props.
   */
  getConfigProps(destinationObject: { [key: string]: any } = {}): any {
    const allConfig = {
      ...fieldDefaultUtils.getDefaultsforType(this._type as string),
      readOnly: this._readOnly,
      ...this._rawConfig?.config,
      ...this._additionalRegisteredProps,
      ...this._inheritedConfig
    };

    const fallbackStrategy = ConfigFallbackFactory.getStrategy(this._type as string);
    fallbackStrategy?.applyFallback(allConfig);

    this.populateSuggestionProps(allConfig);
    this.resolveConfigProps(allConfig, destinationObject);

    return Utils.resolveStringBooleans(destinationObject);
  }

  /**
   *
   * @param localeReference default value is set to Component's type,
   * @returns LocalizationService instance for the combination of localeReference, path
   * @example <caption>getPConnect().getLocalizationService()</caption>
   * const localizationService = getPConnect().getLocalizationService();
   * localizationService.getLocalizedText('label');
   */
  getLocalizationService(localeReference?: string): LocalizationService {
    const componentName = this.getComponentName();
    const name = localeReference || (componentName === 'View' ? this._rawConfig?.config?.template : componentName);
    const locale = environmentInfo.getLocale() ?? 'en-US';
    const l10nService = new LocalizationService(locale, name);
    const caseLocaleReference = this.getCaseLocaleReference();
    if (caseLocaleReference) {
      l10nService.setLookupList([caseLocaleReference]);
    }
    return l10nService;
  }

  /**
   * Returns the object with raw configuration of the
   * component along with inherited configuration and
   * additional registered props.
   *
   * @example <caption>getPConnect().getRawConfigProps()</caption>
   * const rawProps = getPConnect().getRawConfigProps();
   *
   * @returns Raw component props.
   *
   * @private
   */
  getRawConfigProps(): any {
    const allConfig = {
      ...fieldDefaultUtils.getDefaultsforType(this._type as string),
      ...this._rawConfig?.config,
      ...this._additionalRegisteredProps,
      ...this._inheritedConfig
    };
    this.populateSuggestionProps(allConfig);
    return allConfig;
  }

  /**
   * Returns the resolved props from the provided input props.
   * @function resolveConfigProps
   * @example
   * Example:
   * {
   *  label: "@P .FirstName"
   * }
   * output:
   * {
   *  label: "Hello Name"
   * }
   * @param configProps Props meant to be resolved.
   * @param destinationObject the object to which config props gets added.
   * @example <caption>getPConnect().resolveConfigProps()</caption>
   * const props = getPConnect().resolveConfigProps({ label: `@P .FirstName` });
   *
   * @returns resolved props.
   * @public
   */
  resolveConfigProps(configProps: any, destinationObject: { [key: string]: any } = {}) {
    if (configProps) {
      ContextProcessor.resolveProps(configProps, destinationObject, this);
      AnnotationProcessor.handleComponentMapping(
        destinationObject,
        {
          type: this.getComponentName() || '',
          config: configProps
        },
        this
      );
    }
    return destinationObject;
  }

  /**
   * Returns the resolved props from the provided input props and registers them to configuration object which are used for component rendering.
   * When you call the getConfigProps API, the resolved properties are returned.
   *
   * @function registerAdditionalProps
   * @example
   * Example:
   * additionalMeta = {
   *  label: "@P .FirstName"
   * }
   * output:
   * {
   *   label: "Hello Name"
   * }
   * @param additionalMeta Props meant to be resolved.
   * @example <caption>getPConnect().registerAdditionalProps()</caption>
   * const props = getPConnect().registerAdditionalProps({ label: `@P .FirstName` });
   * the props variable will be an object with 'label' value is field value entered for FirstName field.
   * Also this prop ('label') is registered in component configuration for further usage in re-renders.
   * In this example, the API resolves the FirstName property, obtains the first name value, and registers it to label.
   *
   * @returns resolved props.
   * @public
   */
  registerAdditionalProps(additionalMeta = {}): any {
    Object.assign(this._additionalRegisteredProps, additionalMeta);
    resolveStateProps(additionalMeta, this._stateProps);
    return this.resolveConfigProps(this._additionalRegisteredProps);
  }

  /**
   * This API deregisters the input properties passed from metaConfig.
   * When you call the getConfigProps API, the deregistered properties are not obtained.
   *
   * @param deRegisterKeys The props that needs to be deregistered.
   * @function deRegisterAdditionalProps
   * @example <caption>getPConnect().deRegisterAdditionalProps()</caption>
   * getPConnect().deRegisterAdditionalProps(['label']);
   * @returns Returns True after unregistering
   * @public
   *
   */
  deRegisterAdditionalProps(deRegisterKeys: any[] = []): boolean {
    deRegisterKeys.forEach((key) => {
      delete this._additionalRegisteredProps[key];
    });
    return true;
  }

  /**
   * Populates any additional properties which are specific to component which are not part of component configuration.
   *
   * @function populateAdditionalProps
   * @param configData an object which has props from configuration props data, this objects get populated with new additional properties.
   * @example <caption>Usage: getPConnect().populateAdditionalProps({ label: 'label-name' })  </caption>
   * Example Usage:
   * const configProps = { label: 'label-name' }
   * getPConnect().populateAdditionalProps(configProps);
   *
   * After the above populateAdditionalProps function call, the configProps object value gets updated based on type of the component
   * all the possible keys added to the configObject are:
   * configObject.pageMessages (string) - page messages are added for case_view template.
   * configObject.validatemessage (string) - this key is added for components which are editable and when there is any validation message to be shown.
   * configObject.infoMessage (string) - same as above for editable component which has a warning message to be shown.
   * configObject.status (string) - "error" or "warning" string values.
   * configObject.httpMessages (string) - If there are any top (app) level HTTP messages to be displayed
   * configObject.caseMessages (string) - If there are any cases level messages to be shown.
   * configObject.stages (object) - contains a particular stage info.
   * configObject.fieldMetadata (object) - this object contains the information about the extra configuration which is configured in PRPC.
   *
   * @public
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  populateAdditionalProps(configData: any) {
    const getLocalizedMessages = (messages?: Message[]): Message[] | undefined =>
      messages?.map((msg) => ({
        ...msg,
        message: this.getLocalizationService()?.getLocalizedMessage(msg.message) ?? msg.message
      }));

    const type = this.getComponentName();
    const componentConfig = this.getComponentConfig();
    const isNonPortalRootContainer = type === constants.ROOT_CONTAINER && configData.renderingMode !== 'portal';
    // Preparing pageMessages prop for FlowContainer/ ModalViewContainer
    if (type === constants.MODAL_VIEW_CONTAINER || type === constants.CASE_CREATE_STAGE) {
      const context = ContainerUtils.getActiveContainerItemName(`${constants.APP.APP}/${this.getContainerName()}`);
      const pageMessages = MessageManager.getMessages({
        context,
        category: publicConstants.PAGE
      });
      configData.pageMessages = getLocalizedMessages(pageMessages);
      this._stateProps.pageMessages = configData.pageMessages;
    }

    if (this.isEditable()) {
      // Add field level validation messages to editable components
      let { value: property } = this.getStateProps();
      if (property === undefined && this.getComponentConfig()?.selectionMode === 'multi') {
        property =
          this.getStateProps().selectionList ||
          AnnotationProcessor.trimAnnotation(this.getRawConfigProps().selectionList);
      } else if (
        !property &&
        this.getComponentConfig()?.renderMode === 'Editable' &&
        this.getStateProps().referenceList
      ) {
        property = this.getStateProps().referenceList;
      }

      const errorMessages = MessageManager.getMessages({
        property,
        pageReference: this.getPageReference(),
        context: this.getContextName(),
        type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR
      });

      const infoMessages = MessageManager.getMessages({
        property,
        pageReference: this.getPageReference(),
        context: this.getContextName(),
        type: publicConstants.MESSAGES.MESSAGES_TYPE_INFO
      });

      if (errorMessages && errorMessages[0]) {
        configData.validatemessage =
          errorMessages
            .map((errorMsg: any) =>
              this.getLocalizationService().getLocalizedMessage(errorMsg.message).replace(/\.$/, '')
            )
            .join('. ') + (errorMessages[errorMessages.length - 1].message.endsWith('.') ? '.' : '');
        configData.status = constants.FIELD_LEVEL_ERROR;
        this._stateProps.validatemessage = configData.validatemessage;
        this._stateProps.status = configData.status;
        const requiredErrorMsg = errorMessages?.find(
          (msg: { type: string; subType: string }) => msg.type === 'error' && msg.subType === 'required'
        );
        if (requiredErrorMsg && !configData.required) {
          this.clearErrorMessages({ property, subType: 'required' });
        }
      }

      if (infoMessages && infoMessages[0]) {
        configData.infoMessage = infoMessages[0].message;
        configData.status = constants.FIELD_LEVEL_WARNING;
        this._stateProps.infoMessage = configData.infoMessage;
        this._stateProps.status = configData.status;
      }
    }

    if (type === constants.ROOT_CONTAINER) {
      const httpMessages = MessageManager.getRootContainerHttpMessages(
        isNonPortalRootContainer,
        ContainerUtils.getActiveContainerItemName(`${constants.APP.APP}/${this.getContainerName()}`) as string
      );
      configData.httpMessages = Array.isArray(httpMessages) ? getLocalizedMessages(httpMessages) : httpMessages;
      this._stateProps.httpMessages = configData.httpMessages;

      const isLoggedOut = StateUtils.getSharedState('isLoggedOut');
      configData.isLoggedOut = isLoggedOut;
      this._stateProps.isLoggedOut = isLoggedOut;

      const pageMessages = MessageManager.getMessages({
        context: constants.APP.ROOT,
        category: 'PAGE'
      });
      configData.pageMessages = getLocalizedMessages(pageMessages);
      this._stateProps.pageMessages = configData.pageMessages;
    }

    if (type === constants.HYBRID_VIEW_CONTAINER) {
      const httpMessages = MessageManager.getMessages({
        context: constants.APP.APP,
        category: constants.HTTP
      });
      configData.httpMessages = getLocalizedMessages(httpMessages);
      this._stateProps.httpMessages = httpMessages;
    }

    if (type === constants.APP_SHELL && configData.portalDocMode === constants.CONTAINER_TYPE.MULTIPLE) {
      configData.activeCases = CaseAPIs.getOpenedCaseItems(`${publicConstants.APP.APP}/${constants.PRIMARY}`);
      this._stateProps.activeCases = configData.activeCases;
    }

    if (type === constants.APP_SHELL) {
      const appContext = constants.APP.APP;
      const activeContext = ContainerUtils.getActiveContainerItemName(`${appContext}/${this.getContainerName()}`);

      const messages = [
        ...(MessageManager.getMessages({ context: appContext, category: 'PAGE' }) || []),
        ...(MessageManager.getMessages({ context: activeContext, category: 'PAGE' }) || [])
      ];

      const pageMessages = messages.length > 0 ? messages : undefined;
      configData.pageMessages = pageMessages && getLocalizedMessages(pageMessages);
      this._stateProps.pageMessages = configData.pageMessages;
    }

    if (configData.value && type === constants.SCALARLIST) {
      this._stateProps.value = configData.value;
    }

    if (
      type === constants.APP_SHELL ||
      type === constants.MODAL_VIEW_CONTAINER ||
      type === constants.CASE_CREATE_STAGE ||
      componentConfig.template === constants.CASE_PREVIEW
    ) {
      let httpMessages = MessageManager.getMessages({
        context: constants.APP.APP,
        category: 'HTTP'
      });

      const activeContainerHttpMessages = MessageManager.getMessages({
        context: ContainerUtils.getActiveContainerItemName(`${constants.APP.APP}/${this.getContainerName()}`),
        category: 'HTTP'
      });

      if (httpMessages) {
        if (activeContainerHttpMessages) {
          httpMessages = httpMessages.concat(
            MessageManager.getMessages({
              context: ContainerUtils.getActiveContainerItemName(`${constants.APP.APP}/${this.getContainerName()}`),
              category: 'HTTP'
            })
          );
        }
      } else {
        httpMessages = MessageManager.getMessages({
          context: ContainerUtils.getActiveContainerItemName(`${constants.APP.APP}/${this.getContainerName()}`),
          category: 'HTTP'
        });
      }

      configData.httpMessages = httpMessages
        ? httpMessages.map((msg: any) => this.getLocalizationService().getLocalizedMessage(msg.message))
        : httpMessages;
      this._stateProps.httpMessages = configData.httpMessages;
      httpMessages?.forEach((msg: any) => {
        if (msg.errorDetails) {
          configData.errorDetails = configData.errorDetails
            ? configData.errorDetails.push(...msg.errorDetails)
            : msg.errorDetails;
          this._stateProps.errorDetails = configData.errorDetails;
        }
        if (msg.correlationID) {
          configData.correlationID = msg.correlationID;
        }
      });
    }

    if (
      type === constants.VIEW_CONTAINER ||
      type === constants.FLOW_CONTAINER ||
      type === constants.MODAL_VIEW_CONTAINER ||
      type === constants.PREVIEW_VIEW_CONTAINER ||
      type === constants.ROOT_CONTAINER
    ) {
      configData.routingInfo = RoutingManager.getRoutingInfo(this.getContainerName(), this._contextName);
      if (type === constants.ROOT_CONTAINER) {
        configData.rootConfig = C11nEnvUtils.getItemView(configData.routingInfo, configData.renderingMode);
        this._stateProps.rootConfig = configData.rootConfig;
      }
      this._stateProps.routingInfo = configData.routingInfo;
      if (type === constants.FLOW_CONTAINER) {
        const caseMessages = MessageManager.getMessages({
          context: this.getContextName(),
          category: constants.CASEMESSAGES
        });
        const contextName = this.getContextName();
        configData.caseMessages = caseMessages;
        this._stateProps.caseMessages = configData.caseMessages;
        configData.caseViewMode = this.getValue('context_data.caseViewMode');
        this._stateProps.caseViewMode = configData.caseViewMode;
        const childContainerId = ContainerUtils.getChildContainers(contextName)[0];

        if (childContainerId) {
          const activeWorkarea = ContainerUtils.getActiveContainerItemName(childContainerId);
          const stateData = StateManager?.getStore()?.getState()?.data;
          if (stateData && activeWorkarea) {
            configData.lastUpdateTime = stateData[activeWorkarea]?.caseInfo?.lastUpdateTime;
            this._stateProps.lastUpdateTime = stateData[activeWorkarea]?.caseInfo?.lastUpdateTime;
          }
          // add validation page messages to flow container
          const activeContainerPageMessages = MessageManager.getMessages({
            context: activeWorkarea,
            category: publicConstants.PAGE
          });
          const activeContainerHTTPMessages = MessageManager.getMessages({
            context: activeWorkarea,
            category: 'HTTP'
          });
          configData.httpMessages = getLocalizedMessages(activeContainerHTTPMessages);
          configData.pageMessages = getLocalizedMessages(activeContainerPageMessages);
          this._stateProps.pageMessages = configData.pageMessages;
          this._stateProps.httpMessages = configData.httpMessages;
          activeContainerHTTPMessages?.forEach((msg: any) => {
            if (msg.errorDetails) {
              configData.errorDetails = configData.errorDetails
                ? configData.errorDetails.push(...msg.errorDetails)
                : msg.errorDetails;
              this._stateProps.errorDetails = configData.errorDetails;
            }
            if (msg.correlationID) {
              configData.correlationID = msg.correlationID;
            }
          });
        }
      }
      configData.loadingInfo = this.getLoadingStatus(this.getContextName());
      configData.assignmentNames = this.getCaseInfo()
        .getAssignments()
        ?.map((e: any) => {
          return e.name;
        });
      configData.childAssignmentNames = this.getCaseInfo()
        .getChildAssignments()
        ?.map((e: any) => {
          return e.name;
        });
      if (type === constants.MODAL_VIEW_CONTAINER) {
        // get loadingInfo of context inside ModalViewContainer
        const accessedOrder = configData.routingInfo?.accessedOrder || [];
        const key = accessedOrder[accessedOrder.length - 1];
        if (key) configData.loadingInfo = this.getLoadingStatus(key);
      }
      this._stateProps.loadingInfo = configData.loadingInfo;
      this._stateProps.assignmentNames = configData.assignmentNames;
      this._stateProps.childAssignmentNames = configData.childAssignmentNames;
    } else if (
      componentConfig.template === constants.CASE_VIEW ||
      componentConfig.template === constants.SELF_SERVICE_CASE_VIEW ||
      componentConfig.template === constants.CASE_PREVIEW ||
      componentConfig.template === constants.OBJECT_PAGE
    ) {
      configData.caseInfo = this.getCaseSummary();
      this._stateProps.caseInfo = configData.caseInfo;
    } else if (type === constants.CASE_STAGES) {
      configData.stages = this.getCaseStages();
      configData.stageID = this.getStageID();
      this._stateProps.stages = configData.stages;
      this._stateProps.stageID = configData.stageID;
    }
    const shouldAutoRefresh = this.getInheritedProps()?.shouldAutoRefresh as boolean;
    if (shouldAutoRefresh && this.meta?.config?.value?.includes('@P') && this.meta.config._autoRefresh_ === undefined) {
      this.meta.config._autoRefresh_ = `@REFRESH ${this.meta.config?.value.split('@P ')[1]}`;
    }
    this.populateFieldMetadata(configData);
    this.updateContextTree();
  }

  populateFieldMetadata(configData: any) {
    // Add fieldMetadata prop for non-nullish values.
    const { value, datasource, listType } = this.getComponentConfig(); // {value: "@P .Fullname.firstName"}
    const { propertyValue, propertyAnnotation } = this.getPropertyDetails(listType, datasource, value);
    if (
      propertyValue &&
      typeof propertyValue === 'string' &&
      (propertyValue.includes(propertyAnnotation) || propertyValue.includes(constants.ATTACHMENT_ANNOTATION))
    ) {
      // const propertyName = propertyValue.split(".").slice(1).join("."); //@P .Fullname.firstname to .Fullname.firstname
      const propertyName = AnnotationProcessor.getPropertyName(propertyValue);
      const fieldMetadata = this.getFieldMetadata(propertyName);
      if (fieldMetadata) {
        this.updateConfigData(configData, fieldMetadata, propertyName);
      }
    }
  }

  /**
   * @private
   * @param propertyMetadata
   */
  resolvePropertyMetadata(propertyMetadata: PropertyModel | PropertyModel[] | null, propName?: string) {
    if (!propertyMetadata) {
      return propertyMetadata;
    }
    const additionalInfo = getAdditionalInfo(`${this._pageReference}.${propName}`, this._contextName);
    if ((propertyMetadata as PropertyModel)?.additionalInformation) {
      this.setStateProps({ additionalInformation: additionalInfo });
    }
    if (!Array.isArray(propertyMetadata)) {
      (propertyMetadata as PropertyModel).additionalInformation = additionalInfo;
    }
    return propertyMetadata;
  }

  /**
   * Returns the field model (single model object (or) Array of model objects) (or) null.
   * field model is the information like class it belongs to, editValidate, useValidate, maxLength, etc which are configured on the field.
   * Model becomes an array if the field belongs to more than one class, otherwise we get only one object.
   *
   * @param propertyName property (field) name without dot.
   * @returns - { classID: 'class-1', type: "text"} | {{ classID: 'class-1', type: "text"}, { classID: 'class-1', type: "text", editValidate: 'funcName'}}
   * @example <caption>Usage of getFieldMetadata</caption>
   * Ex: const firstNameMetaData = getPConnect().getFieldMetadata('FirstName');
   *
   * firstNameMetaData will receive the configured information on the field while configuring it.
   *
   * @private
   */
  getFieldMetadata(propertyName: string): any {
    if (!propertyName) {
      return null;
    }
    const propertyNameSplit = propertyName.split('.');
    if (propertyNameSplit.length < 2) {
      let classID = this.getValue(constants.CLASS_ID);
      if (!classID && this._rawConfig && this._rawConfig.config && this._rawConfig.config.ruleClass) {
        classID = this._rawConfig.config.ruleClass;
      }
      return this.resolvePropertyMetadata(getPropertyMetadata(propertyName, classID), propertyName);
    }
    const propertyNameValue = propertyNameSplit.pop();
    const propertyNamePrefix = propertyNameSplit.join('.') ? '.' : '';
    let classID = this.getValue(`${propertyNamePrefix}${propertyNameSplit.join('.')}${constants.CLASS_ID}`);
    if (!classID) {
      classID = Utils.resolvePageClass(propertyName, this.getValue(constants.CASE_INFO.CASE_INFO_CLASSID));
    }
    return this.resolvePropertyMetadata(getPropertyMetadata(propertyNameValue, classID), propertyName);
  }

  /**
   * Returns the current page meta data.
   *
   * @param contextPageReference context page reference.
   * @returns - { classID: 'class-1', type: "text"} | {{ classID: 'class-1', type: "text"}, { classID: 'class-1', type: "text", editValidate: 'funcName'}}
   * @example <caption>Usage of getCurrentPageFieldMetadata</caption>
   * Ex: const firstNameMetaData = getPConnect().getCurrentPageFieldMetadata();
   *
   * firstNameMetaData will receive the configured information on the page while configuring it.
   *
   * @private
   */
  getCurrentPageFieldMetadata(contextPageReference: string | null = null): any {
    let pageRefs = this.getPageReference().split('.');
    if (contextPageReference !== null) {
      pageRefs = contextPageReference.split('.');
    }
    const fieldName = pageRefs.pop();

    return this.resolvePropertyMetadata(
      getPropertyMetadata(fieldName, this.getValue(`${pageRefs.join('.')}${constants.CLASS_ID}`))
    );
  }

  /**
   * Returns the current field classId
   * @returns - "class-name"
   * @function getCurrentClassID
   * @example <caption>Usage of getCurrentClassID</caption>
   * Ex: const firstNameClassId = getPConnect().getCurrentClassID();
   *
   * firstNameClassId will receive the class id corresponding to the field.
   *
   * @public
   */
  getCurrentClassID(): string {
    return this._classID ?? this.getValue(constants.CLASS_ID);
  }

  /**
   * Returns case summary and isCaseWideAction function consist of below structure
   * @function getCaseSummary
   * @example
   * {
   * routedTo:"user@constellation.com"
   * urgency:"10"
   * caseID:"OPB1HW-SPACE-WORK RA-219"
   * name:"Request approval"
   * caseClass:"OPB1HW-SpaceTra-Work-RequestApproval"
   * ID:"ASSIGN-WORKLIST OPB1HW-SPACE-WORK RA-219!REQUEST_FLOW_0"
   * isCaseWideAction
   * }
   *
   * isCaseWideAction() function returns true is the action is a case action else false
   */
  getCaseSummary(): any {
    const caseInfo = {
      ...this.getDataObject()[constants.getProperty(publicConstants.CASE_INFO.CASE_INFO)]
    };
    caseInfo.CaseID =
      caseInfo.CaseID || this.getValue(constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_PZINSKEY));

    return {
      ...caseInfo,
      isCaseWideAction: () => {
        return CaseUtils.isCaseWideAction(this.getContextName(), this.getPageReference());
      }
    };
  }

  /**
   * Returns referencedUsers in view with the below structure
   *
   * @function getReferencedUser
   * @param userId is userIdentifier of a user.
   * {
   * "operatorName": {
   *   "UserName": "Chandra"
   *  }
   * }
   */
  getReferencedUser(userId: string): any {
    const refUsers = this.getValue('referencedUsers');

    if (Array.isArray(refUsers)) {
      const userObj = refUsers.find((user) => user.UserID === userId);
      if (userObj) {
        return {
          userId: userObj.UserID,
          UserName: userObj.UserName
        };
      }
    }

    return refUsers && refUsers[userId] ? refUsers[userId] : null;
  }

  /**
   * Returns case stages consist of below structure
   * @function getCaseStages
   * @example
   * {
   * ID: "PRIM1"
   * entryTime: "20200520T093921.867 GMT"
   * links: {,…}
   * name: "Collect Info"
   * type: "Primary"
   * visited_status: "active"
   * }
   */
  getCaseStages() {
    return this.getDataObject()[publicConstants.CASE_INFO.CASE_INFO].stages;
  }

  getStageID() {
    return this.getDataObject()[publicConstants.CASE_INFO.CASE_INFO].stageID;
  }

  /**
   * Gets the actions object that is used to configure a component’s event actions.
   * @function getActions
   * @example <caption>Example for getActions()</caption>
   * Example usage - const actions = getPConnect().getActions(); return <Currency value={value} label={label} {...actions} />;
   * // the API provides the currency component’s action-set.
   *
   * @returns actions configured
   */
  getActions(): any {
    return this._actions;
  }

  /**
   * Returns true if this C11nEnv is the root
   * @returns True is this is the root
   * @private
   */
  isRoot(): boolean | undefined {
    return this._isRoot;
  }

  /**
   * Checks and resolves any context related info.
   * like, any errors for properties and any validation
   * info for this component.
   * @param target target object to update the context.
   * @private
   */
  resolveContextProps(target: { error: string; validatemessage: string }) {
    const { value } = this.getStateProps();
    if (value) {
      target.error = this.getContextDataValue(`${this.getPageReference() + value}.error`);
      target.validatemessage = this.getContextDataValue(`${this.getPageReference() + value}.validatemessage`);
    }
  }

  /**
   * Checks whether validators configured for this
   * component or not.
   * @returns true or false
   * @private
   */
  isValidatorExist(): boolean {
    return !!this._isValidatorExist;
  }

  /**
   * Obtains an entry point to the {@link ValidationApi} object that contains APIs that handle validations on the field.
   * @function getValidationApi
   * @returns entry point to the {@link ValidationApi}
   *
   * @example <caption> Example for getValidationApi() </caption>
   * const validationApi = pConn.getValidationApi();
   * @public
   */
  getValidationApi(): ValidationApi {
    return new ValidationApi(this);
  }

  /**
   * Checks whether a component is editable, relies on value attribute
   *
   * @function isEditable
   * @example <caption> Example for isEditable() </caption>
   * const isEditable = pConn.isEditable();
   * where isEditable is the boolean that contains true/false
   * In this example, the API checks if a component can be edited and returns the resulting boolean value.
   *
   * @returns true or false
   */
  isEditable(): boolean {
    return !!(
      this._rawConfig &&
      this._rawConfig.config &&
      !(this._inheritedConfig && this._inheritedConfig.readOnly) &&
      /* selectionList field OR Editable referenceList */
      (Object.hasOwn(this._rawConfig.config, 'value') ||
        this._rawConfig.config.selectionList ||
        (this._rawConfig.config.renderMode === 'Editable' && this._rawConfig.config.referenceList)) &&
      /* FilteredList field */
      !(typeof this._rawConfig.config?.value === 'string' && this._rawConfig.config.value.includes('@FILTERED_LIST')) &&
      /* readOnly field */
      !(
        Object.hasOwn(this._rawConfig.config, 'readOnly') &&
        (this._rawConfig.config.readOnly === 'true' || this._rawConfig.config.readOnly === true)
      ) &&
      /* disabled field */

      !(
        Object.hasOwn(this._rawConfig.config, 'disabled') &&
        /* istanbul ignore next */ this._rawConfig.config.disabled === true
      )
    );
  }

  isVisible(): boolean {
    if (
      this._rawConfig?.config &&
      Object.hasOwn(this._rawConfig?.config, 'visibility') &&
      typeof this._rawConfig?.config?.visibility === 'string'
    ) {
      return AnnotationProcessor.handleAnnotation(this._rawConfig?.config?.visibility, this) as boolean;
    }
    // for type reference, checking visibility from options
    if (typeof this.options?.visibility === 'string') {
      return AnnotationProcessor.handleAnnotation(this.options?.visibility, this) as boolean;
    }
    return true;
  }

  isReadOnly(): boolean {
    if (this._readOnly === undefined) {
      return false;
    }
    if (this._readOnly === 'true' || this._readOnly === true) {
      return true;
    }
    if (typeof this._readOnly === 'string') {
      return AnnotationProcessor.handleAnnotation(this._readOnly, this) === true;
    }
    return false;
  }

  /**
   * This API checks if the current component has children.
   *
   * @function hasChildren
   * @example <caption> Example for hasChildren - getPConnect().hasChildren() </caption>
   * Usage: const componentHasChildren = getPConnect().hasChildren();
   * componentHasChildren will be true or false indicating whether current component has children in the component configuration.
   *
   * @returns true or false
   * @public
   */
  hasChildren(): boolean {
    return !!this._children;
  }

  /**
   * Returns whether the node is visible && has some visible children
   * (if it is of a type which require children).
   * @returns whether the node should be visible.
   * @private
   */
  getComputedVisibility(resolvedViews: string[] = []): boolean {
    const isReferenceView = this._type === 'reference' && !!this.getReferencedView();
    const pConnect = isReferenceView ? this.getReferencedViewPConnect().getPConnect() : this;

    if (isReferenceView) {
      const referenceKey = `${this.getRawMetadata()?.config?.name}!${this._ruleClass}`;
      if (resolvedViews.includes(referenceKey)) {
        return false;
      }
      resolvedViews.push(referenceKey);
    }

    if (this.getConfigProps().visibility === false) {
      return false;
    }

    // For backward compatibility, need to support both setting on the view or the reference config
    if (
      pConnect.getConfigProps().requiresVisibleChildren === false ||
      this.getConfigProps().requiresVisibleChildren === false
    ) {
      // Item is visible and configured to show without children.
      return true;
    }

    const configProps = pConnect.getConfigProps();

    const requireChildren = {
      includeTypes: ['Group', 'Region', 'View'],
      excludeTemplates: ['ListView', 'SimpleTable', 'StudioTable']
    };
    let requiresVisibleChildren =
      pConnect._type &&
      requireChildren.includeTypes.includes(pConnect._type) &&
      !requireChildren.excludeTemplates.includes(configProps.template);

    const hasVisibleHighlightedData =
      !!configProps.highlightedData && configProps.highlightedData.length && configProps.showHighlightedData;
    if (hasVisibleHighlightedData) {
      requiresVisibleChildren = false;
    }

    if (!requiresVisibleChildren) {
      return true;
    }

    const children = pConnect.getChildren();

    if (!children) {
      return false;
    }

    return children.some((c: any) => c.getPConnect().getComputedVisibility(resolvedViews));
  }

  /**
   * Returns the children iterator
   * @returns iterator
   * @private
   */
  getChildrenIterator(): any {
    this._children.setContext(this.getPageReference());
    return this._children;
  }

  /**
   * This API assigns a new action(based on the event type and handler)to the list of the actions
   * associated with the current component.
   *
   * @function setAction
   * @example <caption> Example for setAction() </caption>
   * Example usage - setAction("onChange", changeHandler);
   * // this API associates the change handler to the onChange event type, and assigns the new action to the list of the actions associated with the current component.
   *
   * @param eventType The type of the event that determines the new action to be added to the list of actions.
   * @param handler The function that must be associated with the event type.
   *
   * @public
   */
  setAction(eventType: string, handler: Function) {
    this._actions[eventType] = handler;
  }

  /**
   * Returns the component name example Button.
   *
   * @function getComponentName
   * @example <caption>getPConnect().getComponentName()</caption>
   * const component = getPConnect().getComponentName();
   *
   * @returns current component name.
   */
  getComponentName() {
    return this._rawConfig && this._rawConfig.type;
  }

  /**
   * Checks whether current component is refreshable
   * or not.
   * Example:
   * If section is configured with refresh this section
   * then section will be treated as refreshable.
   * @returns true or false
   * @private
   */
  isComponentRefreshable() {
    return !!(this._rawConfig && this._rawConfig.isRefreshable);
  }

  /**
   * this function helps to obtain the data of the current context from store.
   * Here `data` may contain below information
   *  <ul>
   *    <li>caseInfo</li>
   *    <li>pulse</li>
   *    <li>pxRequestor</li>
   *    <li>Data Pages</li>
   *    <li>...</li>
   *  </ul>
   *
   * @public
   * @function getDataObject
   * @param contextName qualified context name e.g. app/primary_1
   * @returns data object
   *
   * @example <caption> Example for getDataObject() </caption>
   * const data = pConn.getDataObject();
   * const { caseInfo, pulse } = data;
   *
   */
  getDataObject(contextName?: string): any {
    return StateManager.getDataObject(contextName || this.getContextName());
  }

  /**
   * Returns the resolved image path, it resolves the image with current
   * static service.
   *
   * @function getImagePath
   * @example <caption>getPConnect().getImagePath(imageName)</caption>
   * const imagePath = getPConnect().getImagePath("launcher.png");
   *  // output: imagePath -> https://service/image/launcher.png
   * @param imageName image name
   * @returns image path
   */
  getImagePath(imageName: string) {
    return `${AssetLoader.getConstellationServiceUrl()}/v860/${AssetLoader.getAppAlias()}/image/${imageName}?org=${RouteParser.getOrganizationName()}`;
  }

  skipRequiredValidation() {
    return this.options.isBulkAction;
  }

  isBulkAction() {
    return this.options.isBulkAction;
  }

  /**
   * Returns list of children for the current component.
   * And also resets the iterator to start-index.
   *
   * @function getChildren
   * @example <caption>Example usage of getPConnect().getChildren()</caption>
   * const children = getPConnect().getChildren()
   * the children variable will be receiving the list of children components of the current component.
   *
   * @returns returns the list of children
   * @public
   */
  getChildren(): { getPConnect: () => C11nEnv }[] {
    if (this._children && this._children.length) {
      const shouldAutoRefresh = this.options?.inheritedProps?.find(
        (inheritedProp) => inheritedProp?.prop === 'shouldAutoRefresh'
      );
      if (shouldAutoRefresh) {
        this._inheritedConfig._inheritedProps = [...(this._inheritedConfig._inheritedProps || []), shouldAutoRefresh];
      }
      const childrenIterator = getChildrenIterator(this._rawConfig, {
        pageReference: this._pageReference,
        context: this._contextName,
        localeReference: this._localeRuleName,
        hasForm: this.options.hasForm,
        isBulkAction: this.options.isBulkAction,
        containerName: this.getContainerName(),
        containerItemName: this.options.containerItemName,
        // Passing the view name to the iterator. This again comes to c11nEnv in the options object when the object for a child view is created.
        viewName: this.viewName,
        inheritedConfig: this._inheritedConfig,
        target: this._containerTarget,
        readOnly: this._readOnly,
        categoryName: this._type === 'Group' ? this._rawConfig?.config?.id : null
      });
      const childrenArr: { getPConnect: () => C11nEnv }[] = [];
      while (childrenIterator?.hasNext()) {
        const childConfig = childrenIterator.next();
        if (!this.isBulkAction() || !this.#canBulkProcess(childConfig)) {
          childrenArr.push(childConfig);
        }
      }
      childrenIterator?.resetIndex();
      return childrenArr;
    }
    return [];
  }

  /**
   * This API set inherited prop and propagates the prop to all its children.
   *
   * @function setInheritedProp
   * @example <caption>getPConnect().setInheritedProp("displayMode", "DETAILS")</caption>
   *
   * @param propName name of the prop eq., displayMode
   * @param value value to be set ew., LEFT
   *
   */
  setInheritedProp(propName: string, value: any) {
    this._inheritedConfig[propName] = value;
  }

  /**
   * This API returns locale reference rule name this environment object is referencing for the field values.
   *
   * @function getLocaleRuleName
   * @example <caption>getPConnect().getLocaleRuleName()</caption>
   * const localeRuleName = getPConnect().getLocaleRuleName();
   * //Returns "VEHICLE-!VIEW!VEHICLEDETAILS", if the current environment object corresponds to a view named VehicleDetails in Work- class.
   * //The above is the name of localization rule which is 1-1 mapped to a View named VehicleDetails
   *
   * @returns Returns the locale reference rule name.
   */
  getLocaleRuleName() {
    return this._localeRuleName;
  }

  /**
   * Returns the localized value for a string.
   *
   * @function getLocalizedValue
   * @example <caption>getPConnect().getLocalizedValue("First Name", "fields","WORK-!VIEW!CANDIDATEINFO")</caption>
   * If the localization store looks like below.
   *
   * {
   *     "WORK-!VIEW!CANDIDATEINFO" : {
   *          "fields" : {
   *              "First Name" : "Primero nome",
   *              "Last Name"  : "Ultimo nome"
   *           }
   *      }
   * }
   * The API returns "Primero nome" for a rule  "WORK-!VIEW!CANDIDATEINFO".
   *
   * @param rawString      raw string
   * @param localePath     locale path
   * @param localeRuleKey Rule Key in the localization store
   * @returns returns the localized value.
   * @deprecated instead use
   * <code>
   * const l11nService = getLocalizationService(localeRuleKey);
   * l11nService.getLocalizedText(rawString);
   * l11nService.getLocalizedText(another);
   * </code>
   */
  getLocalizedValue(rawString: string, localePath?: string, localeRuleKey?: string): string {
    const ruleKey = localeRuleKey || this.getLocaleRuleName();
    const localeReference = ruleKey && localePath ? `${ruleKey}#${localePath}` : ruleKey;
    const translatedString = this.getLocalizationService(localeReference).getLocalizedText(rawString);
    if (translatedString === rawString) {
      return this.getLocalizationService().getLocalizedText(rawString);
    }
    return translatedString;
  }

  /**
   * Returns the localeRuleName for the given Localization rule keys.
   *
   * @example <caption>const ruleKey = getPConnect().getLocaleRuleName("Work-", "View","CandidateInfo")</caption>
   * Here the ruleKey will be  "Work-!View!CandidateInfo"
   *
   * @param localeClass    Localization rule class
   * @param localeContext  Localization rule context
   * @param localeName     Localization rule name
   * @returns -  localeRuleKey
   * @private
   */
  getLocaleRuleNameFromKeys(localeClass: string, localeContext: string, localeName: string): string {
    return `${localeClass}!${localeContext}!${localeName}`;
  }

  /**
   * this function helps to resolve the property value from the state (Redux store).
   * @function getValue
   * @param prop property whose data value is returned
   * @param pageReference pageReference of parent view
   * @returns property value from the state (Redux store).
   *
   * @example <caption>Example for .firstName property </caption>
   * const firstName = getPConnect().getValue('.firstName');
   * // here firstName variable contains the value retrieved from redux store.
   */
  getValue(prop: string, pageReference?: string): any {
    prop = prop?.replace(/\((\d+)\)/g, (_, index) => {
      const parsedIndex = parseInt(index, 10);
      const decrementedIndex = parsedIndex - 1;
      return `[${decrementedIndex}]`;
    });
    if (this.useCustomContext) {
      prop = prop.startsWith?.('.') ? prop.substring(1) : prop;
      return prop.split?.('.').reduce((context: any, split) => context?.[split] ?? undefined, this.useCustomContext);
    }
    if (this._hasSuggestions && this.getStateProps()?.value === prop) {
      return ContextProcessor.getPropertyValue(
        prop,
        this.getPageReference(),
        `${this._contextName}/${constants.SUGGESTION}`
      );
    }
    return ContextProcessor.getPropertyValue(prop, pageReference || this.getPageReference(), this._contextName);
  }

  setInheritedConfig(rawConfig: any) {
    if (!rawConfig) return;
    const { readOnly, displayMode, referenceType, hideLabel } = rawConfig;

    if (hideLabel) {
      this._inheritedConfig.hideLabel = hideLabel;
    }

    if (referenceType) {
      this._inheritedConfig.referenceType = referenceType;
    }

    if (displayMode) {
      this._inheritedConfig.displayMode = displayMode;
    }

    if (this._inheritedConfig.readOnly || readOnly) {
      this._inheritedConfig.readOnly = readOnly;
    }

    if (rawConfig.inheritedProps) {
      rawConfig.inheritedProps?.forEach((inheritProp: any) => {
        if (inheritProp.prop === 'required') {
          this._inheritedConfig[inheritProp.prop] = inheritProp.value;
        }
      });
      // Copy all props for depth calculation
      mergeInheritedProps(this._inheritedConfig, rawConfig);
    }
  }

  /**
   * This function is used to retrieve props that are set on the parent metadata node within
   * the inheritedProps metadata structure. The purpose of inheritedProps is to allow cascading
   * of values in view metadata.
   *
   * In the following example, a label can be given to a view reference node in order to give a
   * unique label each place a view is included, allowing for better reuse.
   *
   * @function getInheritedProps
   * @returns props object that was inherited from the parent
   * @public
   *
   * @example <caption>Getting a label from parent reference</caption>
   * // Parent reference metadata
   * {
   *   "type": "reference",
   *   "config": {
   *     "name": "Address",
   *     "inheritedProps": [
   *       {
   *         "prop": "label",
   *         "value": "@L Shipping address"
   *       }
   *     ]
   *   }
   * }
   *
   * // Child reusable Address view
   * {
   *   "name": "Address",
   *   "type": "View",
   *   "config": {
   *     "template": "OneColumn",
   *     "ruleClass": "Data-Address"
   *     "label": "Generic address"
   *   },
   *   "children": [
   *     {
   *       "name": "A",
   *       "type": "Region",
   *       "children": []
   *     }
   *   ]
   * }
   *
   * // During render of the child view called Address, one would retrieve the inherited label as follows
   *
   * const propsToUse = { label, ...getPConnect().getInheritedProps() };
   *
   * // This would result in propsToUse.label = “Shipping address”. This inherits the value that was set on the
   * // parent (if the parent had a “label” property) and would override any “label” property that was set on the child view.
   */
  getInheritedProps() {
    const props: { [key: string]: any } = {};
    const inheritedProps: { [key: string]: any } = {};

    this._inheritedConfig.inheritedProps?.forEach((inheritProp: any) => {
      inheritedProps[inheritProp.prop] = inheritProp.value;
    });

    ContextProcessor.resolveProps(inheritedProps, props, this);
    return props;
  }

  /**
   * this function helps to assign the value to property name (or key) in state (Redux store).
   * @function setValue
   * @param prop property name whose data value need to set
   * @param value property value which need to get assigned to the property name.
   * @param initialValue property initialValue which need to get assigned to the property name.
   * @param isImplicit boolean check if it is implicit setValue action
   * @param options object containing extra infomation like isArrayDeepMerge
   * @param options.isArrayDeepMerge flag to decide whether to deep merge the objects or values inside array or not
   *
   * @example <caption>Example for .firstName property </caption>
   * getPConnect().setValue('.firstName', 'lorem ipsum');
   * // here examples shows setting the `lorem ipsum` value in firstName property
   */
  setValue(
    prop: string,
    value: { [key: string]: any } | undefined,
    initialValue?: string,
    isImplicit = false,
    options: { isArrayDeepMerge?: boolean } = {}
  ) {
    let isStrictTracking;
    let removePropertyFromChangedList;
    const { isArrayDeepMerge } = options;
    if (this.isInsideList()) {
      this.getListActions().updateProperty(prop, value);
    }
    if (this.isBulkAction()) {
      isStrictTracking = true;
      if (!value) removePropertyFromChangedList = true;
    }
    StateManager.dispatch({
      type: 'SET_PROPERTY',
      payload: {
        reference: `${this.getPageReference()}${prop}`,
        value,
        initialValue,
        isImplicit,
        context: this.getContextName(),
        ...(isArrayDeepMerge !== undefined && { isArrayDeepMerge }),
        ...(isStrictTracking && { isStrictTracking }),
        ...(removePropertyFromChangedList && { removePropertyFromChangedList })
      }
    });
  }

  /**
   * This function helps to update the value to property name in change list
   * @function updateDirtyCheckChangeList
   * @param prop property name whose data value need to set
   * @param value property value which need to get assigned to the property name
   *
   * @example <caption>Example for .firstName property </caption>
   * getPConnect().updateDirtyCheckChangeList('.firstName', 'lorem ipsum');
   * // here examples shows setting the `lorem ipsum` value in firstName property
   * @private
   */
  updateDirtyCheckChangeList(prop: string, value: string) {
    const changeList = {
      reference: `${this.getPageReference()}${prop}`,
      value,
      context: this.getContextName()
    };
    updateChangeList(changeList);
  }

  /**
   * Return context data value like when results etc.,
   * @example <caption>getPConnect().getContextDataValue(prop)</caption>
   * const value = getPConnect().getContextDataValue('.summary_of_when_conditions__.testWhen');
   *
   * @param prop property whose context data value is returned
   * @param pageReference pageReference of parent view
   * @returns Context data value
   * @private
   */
  getContextDataValue(prop: string, pageReference?: string) {
    return StateManager.getContextValue(pageReference || this.getPageReference(), prop, this._contextName);
  }

  /**
   * this function helps to merge data object in store deeply.
   * If an element at the same key is present in both store and data object,
   * the value from data object will stay in the store.
   *
   * @function updateState
   * @param dataObject - object to be merged into store.
   *
   * @example <caption>Example usage to merge firstName key to store</caption>
   * getPConnect().updateState({
   *  firstName: "Test"
   * });
   */
  updateState(dataObject: any) {
    const index = this._pageReference.indexOf(constants.CASE_INFO.CASE_INFO_CONTENT);
    const rootPageIndex = this._pageReference.indexOf('.');
    let rootPageReference = constants.CASE_INFO.CASE_INFO_CONTENT;
    if (index !== 0) {
      rootPageReference = rootPageIndex > 0 ? this._pageReference.substring(rootPageIndex) : this._pageReference;
    }
    StateManager.dispatch(
      mergeData({
        context: this._contextName,
        data: dataObject,
        rootPageReference
      })
    );
  }

  /**
   * this function helps to replace the state of a property or page in the store.
   * @function replaceState
   * @param prop property or page name whose value need to be replaced
   * @param value  value which need to get assigned to the property or page.
   *
   * @example <caption>Example for .address property </caption>
   * getPConnect().replaceState('.address', {
   *  apartment: "New Heights"
   *  streetName: "Park Street",
   * });
   * @param options.skipDirtyValidation - flag to decide whether the property has to considered in Dirty Validation. Default is false. If passed as true Dirty Validation will be skipped on the respected property.
   * @example <caption>Example for .address property will not be include in the response of formutils.getChanges() API </caption>
   * getPConnect().replaceState('.address', {
   *  apartment: "New Heights"
   *  streetName: "Park Street",
   * }, {skipDirtyValidation: true });
   */
  replaceState(prop: string, value: string, options: { skipDirtyValidation?: boolean } = {}) {
    const { skipDirtyValidation = false } = options;
    StateManager.dispatch({
      type: 'REPLACE_PROPERTY',
      payload: {
        reference: `${this.getPageReference()}${prop}`,
        value,
        context: this.getContextName(),
        skipDirtyValidation
      }
    });
  }

  /**
   * this function helps to set error at Redux store level
   * so that the errors that need to be handled at infra level can make use of this function
   *
   * @public
   * @function reportError
   * @param errorMsg - Error message to be logged to Redux store.
   * @param errorObj - Error object that contains more error info.
   * @param context - Context data value
   *
   * @example <caption>Example usage for reportError function</caption>
   * getPConnect().reportError(
   *    ': Error occurred during ajax call at fetchMessages API :',
   *    {
   *      errorClassification: 'Invalid inputs'
   *      errorDetails: {
   *        localizedValue: 'localized error message'
   *      }
   *    }
   * );
   */
  reportError(errorMsg: string, errorObj: any, context?: string) {
    StateManager.dispatch(error(errorMsg, errorObj || {}, context, errorObj?.formFields));
  }

  /**
   * Returns the env object.
   * @returns env object
   * @private
   */
  getComponent(): any {
    return componentCreator({
      getPConnect: () => this
    });
  }

  /**
   * This API creates a component whose type is provided as part of the meta object and uses the other parameters to resolve the properties and get the context.
   * The definition of the component type which is provided in meta object is upto consumer.
   * @param componentMeta The metadata of the component to be created.
   * @param dataSource The source reference to the Redux store. This contains properties that will be input to the created component.
   * @param index In case of list datasource mention the index.
   * @param additionalPropsToComp Props will be passed to component apart from the configured values.
   *
   * @function createComponent
   * @example <caption>Usage of createComponent: getPConnect().createComponent(meta, dataSource, index, additionalPropsToComp)</caption>
   * Ex: getPConnect().createComponent(
   *  {
   *    type: "Button",
   *    config: {
   *      label: "@P .FirstName"
   *    }
   *  },
   *  "D_Employees.pxResults",
   *  1,
   *  {
   *    active: true
   *  }
   * );
   *
   * Call the createComponent with meta data and necessary arguments, such that the component receives the resolved props to the created component instance.
   * For React: JSX: <Button label="resolved FirstName property value" /> (or) React.createElement('Button', { label: 'resolved FirstName property value })
   *
   * @returns newly created component instance
   *
   * @public
   */
  createComponent(
    componentMeta: Meta,
    dataSource?: string,
    index?: number,
    additionalPropsToComp?: { [key: string]: any }
  ) {
    let pageReference = this._pageReference;
    if (dataSource) {
      const pageListContext = StateManager.lookupContextValue(
        `${this.getPageReference()}.${constants.SUMMARY_OF_LISTS}`,
        dataSource,
        this._contextName
      );
      pageReference = ContextProcessor.getContextPath(pageListContext || dataSource, index) || dataSource;
    } else if (additionalPropsToComp && additionalPropsToComp.pageReference) {
      let referencedPage = additionalPropsToComp.pageReference;
      referencedPage = AnnotationProcessor.handleAnnotation(referencedPage, this);
      pageReference = ContextProcessor.getContextPath(this._pageReference, referencedPage) || '';
    }

    return componentCreator(
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      createC11nEnv({
        meta: componentMeta,
        index: this.index,
        options: {
          containerName: this.getContainerName(),
          hasForm: this.options.hasForm,
          ...componentMeta.options,
          pageReference,
          context: this._contextName,
          target: this._containerTarget,
          readOnly: this._readOnly || this._inheritedConfig?.readOnly,
          viewName: this.viewName
        },
        useCustomContext: this.useCustomContext
      }),
      additionalPropsToComp
    );
  }

  /**
   * This API returns the viewMetadata of the referenced view
   * @function getReferencedView
   * @example <caption>
   * const meta = {
   *  meta: {
   *    type: 'reference',
   *    config: {
   *      type: 'view',
   *      name: 'Address'
   *    }
   *  }
   * }
   * const c11nEnv = createC11nEnv(meta).getPConnect();
   * const viewMetadata = c11nEnv.getReferencedView();
   * 'viewMetadata' is the metadata object of the 'Address' view
   * </caption>
   * @returns referenced view metadata
   */
  getReferencedView(): Meta | undefined {
    if (!this.meta) return undefined;

    return ContextProcessor.resolveReference(this.resolveConfigProps(this.meta), this);
  }

  /**
   *
   * @param {string} context
   * @returns {string} resolved page reference
   * @private
   */
  _resolvePageRef(context: string): string {
    if (this.getPageReference().includes(context) || context.startsWith(constants.CLASS_ANNOTATION)) {
      return this.getPageReference();
    }
    return `${this.getPageReference()}${context}`;
  }

  /**
   * This API returns the PConnect object of the referenced view with in a reference
   * @param passOptions this allows to pass reference's options to the view if it is true
   * @function getReferencedViewPConnect
   * @example <caption>const getPConnect = getPConnect().getReferencedViewPConnect().getPConnect</caption>
   * @returns An PConnect object with getPConnect of the referenced view with in type reference
   *
   * @public
   */
  getReferencedViewPConnect(passOptions = false) {
    const referenceConfig = { ...this.getComponentConfig() };
    const shouldAutoRefreshRef = this.options?.inheritedConfig?._inheritedProps?.find(
      (inheritedProp) => inheritedProp.prop === 'shouldAutoRefresh'
    );

    if (
      shouldAutoRefreshRef &&
      !referenceConfig?.inheritedProps?.some(
        (inheritProp: { [key: string]: string }) => inheritProp.prop === 'shouldAutoRefresh'
      )
    ) {
      referenceConfig?.inheritedProps?.push(shouldAutoRefreshRef);
    }
    delete referenceConfig?.name;
    delete referenceConfig?.type;

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return createC11nEnv({
      meta: this.getReferencedView(),
      options: {
        ...(passOptions ? this.options : undefined),
        ...referenceConfig,
        context: this.getContextName(),
        pageReference: referenceConfig.context
          ? this._resolvePageRef(referenceConfig.context)
          : this.getPageReference(),
        viewName: this.viewName
      }
    });
  }

  /**
   * Returns an entry point to the Container Manager<br>
   * The Container Manager can be used to access any of the APIs found at {@link ContainerManager}
   * @function getContainerManager
   * @returns entry point to the {@link ContainerManager}
   * @example <caption>Example for getContainerManager()</caption>
   * const containerManager = getPConnect().getContainerManager();
   * //any of the container manager APIs can be called on the entry point returned
   */
  getContainerManager() {
    return new ContainerManager(this);
  }

  /**
   * Returns an entry point to the Actions API. This entry point
   * can be used to access any of the APIs found at {@link ActionsApi}
   * @function getActionsApi
   * @example
   * Example usage:
   *   const actionsAPI = pConn.getActionsApi();
   * [...]
   *    actionsApi.showPage(actionInfo);
   *    actionsApi.createWork(actionInfo);
   *
   * where actionInfo is the JSON object that contains the information
   * necessary for the action to be properly invoked.
   *
   * @returns entry point to the {@link ActionsApi}
   */
  getActionsApi() {
    if (!this._actionsApi) {
      this._actionsApi = new ActionsApi(this);
    }
    return this._actionsApi;
  }

  /**
   * Returns the ActionManager object that can be used
   * to access the Core's ActionManager API
   * @returns - the {@link ActionManager} object
   * @private
   */
  getActionManager(): object {
    return ActionManagerInstance;
  }

  /**
   * This function helps to check whether the context exists in store or not
   * @ignore
   * @param context -- context name
   * @returns - true if context exists otherwise false
   * @private
   */
  doesContextExist(context?: string): boolean {
    return StateManager.doesContextExist(context ?? this.getContextName());
  }

  /**
   * used in the Location component of the Nebula layer to obtain the API key
   * for the Google Maps applicationfrom the DSS setting(uiengine/map/googleapikey) and
   * send it to the MapsContext control of the Cosmos layer as a property
   * @function getGoogleMapsAPIKey
   * @returns google maps api key
   * @example <caption>Example for getGoogleMapsAPIKey()</caption>
   * let apiKey = getPConnect().getGoogleMapsAPIKey();
   * // the API key to render Google Maps is obtained
   */
  getGoogleMapsAPIKey() {
    return services.getGoogleMapsAPIKey();
  }

  /**
   * used in the Richtext component of the Nebula layer to obtain the server url.

   * @function  getServerURL
   * @returns server url
   * @example <caption>Example for getServerURL()</caption>
   * let serverurl = getPConnect().getServerURL();
   * // the server url is obtained
   */
  getServerURL() {
    return services.getServerURL();
  }

  /**
   * Returns the loading status of the current container context
   * @function getLoadingStatus
   * @param contextName representing the context for which we need to fetch the loading status
   * @returns true/false : Loading Status of the current container
   * @example <caption>Get the Loading status of the current context container</caption>
   * if(getPConnect().getLoadingStatus()==true){
   * //Code to handle the loading status
   * };
   * @private
   */
  getLoadingStatus(contextName: string): boolean {
    let contextDataObj: { isLoading?: boolean; context_data: any } | null = null;
    if (!contextName) {
      contextDataObj = this.getDataObject().context_data;
    } else {
      contextDataObj = StateManager.getDataObject(contextName);
      contextDataObj = contextDataObj ? contextDataObj.context_data : null;
    }
    if (contextDataObj) {
      return contextDataObj.isLoading ? contextDataObj.isLoading : false;
    }
    return false;
  }

  /**
   * Deletes the error messages associated with a specific context (like an assignment, case flow) or App container.
   * @function clearErrorMessages
   * @param errorMsgConfig The {@link ErrorMsgConfig} object contains information that specifies which error messages are cleared
   *
   * @example <caption>Example for clearing messages on a context</caption>
   * getPConnect().clearErrorMessages({category: "HTTP", context: "app/primary"});
   * This will clear the error messages at the "app/primary" level which belong to category of type HTTP (like 4XX errors).
   *
   * @example <caption>Example for clearing messages on AppShell</caption>
   * getPConnect().clearErrorMessages({category: "HTTP"});
   * This will clear the error messages at the App level which belongs to category HTTP.
   *
   * @example <caption>Example for clearing messages on FirstName property</caption>
   * getPConnect().clearErrorMessages({property: ".firstName"});
   * This will clear the error messages on the property firstName.
   *
   * @public
   */
  clearErrorMessages(errorMsgConfig: ErrorMsgConfig) {
    if (errorMsgConfig?.property) {
      MessageManager.clearMessages({
        type: 'error',
        property: errorMsgConfig.property,
        pageReference: this.getPageReference(),
        context: this.getContextName(),
        ...(errorMsgConfig.subType && { subType: errorMsgConfig.subType })
      });
    } else if (errorMsgConfig?.context) {
      MessageManager.clearMessages({
        type: 'error',
        ...errorMsgConfig
      });
    } else {
      MessageManager.clearMessages({
        type: 'error',
        ...errorMsgConfig,
        context: constants.APP.APP
      });
      MessageManager.clearMessages({
        type: 'error',
        ...errorMsgConfig,
        context: ContainerUtils.getActiveContainerItemName(`${constants.APP.APP}/${this.getContainerName()}`)
      });
    }
  }

  resolveDatasourceReference(datasourceRef: string) {
    const pageListContextData = StateManager.lookupContextValue(
      `${this.getPageReference()}.${constants.SUMMARY_OF_LISTS}`,
      datasourceRef,
      this.getContextName()
    );
    return pageListContextData || datasourceRef;
  }

  /**
   * Removes the field from form map
   * @function removeFormField
   *
   * @example <caption>Example for clearing form on a context</caption>
   * getPConnect().removeFormField();
   * This will clear the form in the current context.
   *
   * @private
   */
  removeFormField() {
    // will delete this function once DX Component calling is removed
  }

  /**
   * Adds the field from form map
   * @private
   */
  addFormField() {
    // will delete this function once DX Component calling is removed
  }

  /**
   * This api is used to set the given sourcePage value to the given target
   * @function setPageValue
   * @param sourcePage source page object which needs to be set to target property name
   * @param target target property name where the source page is being set.
   * @param isPerform isPerform to allow generating Pageinstruction for form submission if applicable.
   * @example <caption>Example for setPageValue</caption>
   * getPConnect().setPageValue({street: 'ABC', city: 'DEF'}, '.EmbeddedPage',true);
   */
  setPageValue(sourcePage: { [key: string]: unknown }, target: string, isPerform: boolean = false) {
    delete sourcePage?.classID;
    this.setValue(target, sourcePage, undefined, undefined, { isArrayDeepMerge: false });
    if (isPerform) {
      generatePageInstructionsForNestedPayload(sourcePage, target, this);
    } else {
      addPageFieldToForm(
        this.getContextName(),
        `${this.getPageReference()}${target}`,
        this.getStateProps()?.value ?? ''
      );
    }
  }

  /**
   * Accepts the value stored in the Suggestions Context for a field.
   * @param target The field whose value stored in the Suggestions Context is accepted.
   * @function acceptSuggestion
   * @example <caption>Example for acceptSuggestion</caption>
   * In this example, the API accepts the value stored in the Suggestions Context for the Property1 field.
   * getPConnect().acceptSuggestion('.Property1');
   * @public
   */
  acceptSuggestion(target: string) {
    if (this.getStateProps()?.value && this.options.hasForm && this._hasSuggestions) {
      const suggestionContext = StateUtils.getSuggestionsContext(this.getContextName());
      const suggestionProp = this.getStateProps()?.value || target;
      const fullSuggestionPropRef = ContextProcessor.getContextPath(this.getPageReference(), suggestionProp);
      const suggestionValue = suggestionContext.getField(fullSuggestionPropRef);
      this._hasSuggestions = false;
      // it will update the field and trigger validation.
      this.getActionsApi().updateFieldValue(suggestionProp, suggestionValue);
      this.getActionsApi().triggerFieldChange(suggestionProp, suggestionValue);
      suggestionContext.removeField(fullSuggestionPropRef, { actionPerformed: 'accepted' });
    }
  }

  /**
   * Ignores the value stored in the Suggestions Context for a field.
   * @param target The field whose value stored in the Suggestions Context is ignored.
   * @function ignoreSuggestion
   *
   * @example <caption>Example for ignoreSuggestion</caption>
   * In this example, the API ignores the value stored in the Suggestions Context for the Property1 field.
   * getPConnect().ignoreSuggestion('.Property1');
   * @public
   */
  ignoreSuggestion(target?: string) {
    if (this.getStateProps()?.value && this.options.hasForm && this._hasSuggestions) {
      const suggestionProp = this.getStateProps()?.value || target;
      const fullSuggestionPropRef = ContextProcessor.getContextPath(this.getPageReference(), suggestionProp);
      this._hasSuggestions = false;
      StateUtils.getSuggestionsContext(this.getContextName()).removeField(fullSuggestionPropRef, {
        actionPerformed: 'ignored'
      });
      const dependencyFields = DatapageDependencyMap.getDependencies(this._contextName, fullSuggestionPropRef);
      dependencyFields?.forEach((dependencyField) => {
        StateUtils.getSuggestionsContext(this.getContextName()).removeField(dependencyField, {
          actionPerformed: 'ignored'
        });
      });
    }
  }

  /**
   * Returns the self referencing data page keys for the property
   * @function getSelfReferencingDatapageKeys
   * @returns array of string : Self referencing data page keys
   * @example <caption>Get the self referencing data page keys for a property</caption>
   * const selfReferencingDatapageKeys = getPConnect().getSelfReferencingDatapageKeys();
   * @private
   */
  getSelfReferencingDatapageKeys(): string[] {
    const selfReferencingDatapageKeys: string[] = [];
    const propertyName = this.getPropertyName();
    const fieldMetadata = this.getFieldMetadata(propertyName);
    const { datasource: { parameters = {} } = {} }: { datasource: { parameters?: { [key: string]: string } } } =
      fieldMetadata || {};
    Object.values(parameters).forEach((param: string) => {
      const [, parentPropName] = param.split('.');
      const referencePropParent = propertyName?.split('.').pop();
      if (parentPropName === referencePropParent) {
        selfReferencingDatapageKeys.push(param.substring(param.lastIndexOf('.') + 1));
      }
    });
    return selfReferencingDatapageKeys;
  }

  /**
   *
   * @param configData - an object, which contains the meta data about the field which helps in taking decision to mark the field as readonly.
   * @param propertyName - the property (field) name.
   * @returns - boolean
   */
  private isReadOnlyField(configData: any, propertyName: string): boolean {
    const isInternalProperty = AnnotationUtils.isPropertyInternal(propertyName);
    const hasDataSource = configData.datasource;
    const isSearchField = this.getComponentConfig().isSearchField;
    const { isClassKey: isClassKeyField, isSpecial: isSpecialField, isDeclarativeTarget } = configData.fieldMetadata;
    const isListTypeNotAssociated = configData.listType !== constants.ASSOCIATED;

    // If the field is used as search field,
    // Then it should not be readonly as search fields are used for filtering data and not for assignment submit.
    if (isSearchField) {
      return false;
    }

    return (
      isDeclarativeTarget ||
      isInternalProperty ||
      (!hasDataSource && (isClassKeyField || isSpecialField) && isListTypeNotAssociated)
    );
  }

  private getPropertyDetails(listType: string, datasource: string, value: string) {
    if (listType === constants.ASSOCIATED) {
      return {
        propertyValue: datasource,
        propertyAnnotation: constants.ASSOCIATED_PROPERTY_ANNOTATION
      };
    }
    return {
      propertyValue: value,
      propertyAnnotation: constants.PROPERTY_ANNOTATION
    };
  }

  private updateConfigData(configData: FieldConfigData, fieldMetadata: FieldMetadata, propertyName: string) {
    configData.fieldMetadata = fieldMetadata;
    if (fieldMetadata.additionalInformation) {
      configData.additionalInformation = fieldMetadata?.additionalInformation;
    }
    if (this.isReadOnlyField(configData, propertyName)) {
      configData.readOnly = true;
      this._stateProps.readOnly = configData.readOnly;
      if (configData.fieldMetadata.isDeclarativeTarget) this.registerAdditionalProps({ readOnly: true });
    }
    /* Make component readOnly if it is a masked property.
      Currently we are getting "isReadOnly" prop as true in the fieldMetadata for masked properties */
    if (configData.fieldMetadata?.isReadOnly) {
      configData.readOnly = true;
      this.registerAdditionalProps({ readOnly: true });
    }
  }

  #canBulkProcess(childConfig: CreateC11nEnv): boolean {
    const type = childConfig.getPConnect().getComponentName() as string;
    const template = childConfig.getPConnect().getConfigProps().template as string;
    return !(
      C11nEnvUtils.allowedComponetsForBulkProcessing[type] === true ||
      (C11nEnvUtils.allowedComponetsForBulkProcessing[type] as { [key: string]: boolean })?.[template] === true
    );
  }
}

export function createC11nEnv(config: Config): CreateC11nEnv {
  const c11EnvObj = new C11nEnv(config);
  return {
    getPConnect: () => c11EnvObj
  };
}

export function registerComponentCreator(creator: Function) {
  componentCreator = creator;
}

export default {
  createC11nEnv,
  registerComponentCreator
};
